import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import emailjs from "@emailjs/browser";
import { contact } from "../data/contact";
import { SectionHead } from "./Services";
import { useLanguage } from "../context/LanguageContext";

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_y9w2uji";
const EMAILJS_PUBLIC_KEY = "E88cAw7lejfskaSih";
const EMAILJS_TEMPLATE_ADMIN = "template_4b5bgiv"; // Contact Us (admin notification)
const EMAILJS_TEMPLATE_REPLY = "template_hy1oghs"; // Auto-Reply (user)

export default function Contact() {
  const { t, dir, lang } = useLanguage();
  const businessTypes = t.businessTypes;
  const serviceOptions = t.serviceOptions;
  const [form, setForm] = useState({
    name: "",
    email: "",
    businessIndex: 0,
    serviceIndex: 0,
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateIndex = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setError("");

    if (!form.name || !form.email || !form.message) return;

    setLoading(true);
    try {
      // First email - admin notification
      const adminParams = {
        from_name: form.name,
        from_email: form.email,
        business_type: businessTypes[form.businessIndex],
        service_needed: serviceOptions[form.serviceIndex],
        message: form.message,
      };

      console.log("Sending admin email with params:", adminParams);
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ADMIN,
        adminParams
      );
      console.log("Admin email sent successfully");

      // Second email - auto-reply to user
      const userParams = {
        from_name: form.name,
        reply_to: form.email,
      };

      console.log("Sending user auto-reply with params:", userParams);
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_REPLY,
        userParams
      );
      console.log("User auto-reply sent successfully");

      setSubmitted(true);
    } catch (err) {
      console.error("EmailJS error details:", {
        message: err.message,
        text: err.text,
        status: err.status,
      });
      setError(t.contact.sendError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      dir={dir}
      lang={lang}
      className="relative py-24 md:py-32 px-5 sm:px-8"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-7xl grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHead
            eyebrow={t.contact.eyebrow}
            title={t.contact.title}
            sub={t.contact.sub}
          />

          <div className="mt-10 space-y-4">
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-4 p-4 border border-white/10 rounded-xl hover:border-agency-gold/50 transition-colors group"
              data-testid="contact-email-card"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-agency-gold/10 text-agency-gold">
                <Mail size={18} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                  {t.contact.emailLabel}
                </div>
                <div className="text-sm group-hover:text-agency-gold transition-colors" dir="ltr">
                  {contact.email}
                </div>
              </div>
            </a>
            <a
              href={contact.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 p-4 border border-white/10 rounded-xl hover:border-emerald-500/50 transition-colors group"
              data-testid="contact-whatsapp-card"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
                <MessageCircle size={18} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                  {t.contact.whatsappLabel}
                </div>
                <div className="text-sm group-hover:text-emerald-400 transition-colors" dir="ltr">
                  {contact.whatsappDisplay}
                </div>
              </div>
            </a>
          </div>

          <p className="mt-10 text-xs text-white/40">
            {t.contact.respondNote}
          </p>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 relative rounded-2xl bg-agency-gray border border-white/10 p-6 md:p-10"
          data-testid="contact-form"
        >
          {submitted ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-center"
              data-testid="contact-success-state"
            >
              <CheckCircle2 size={40} className="text-agency-gold" />
              <h3 className="mt-5 font-display text-3xl font-bold">
                {t.contact.successTitle}
              </h3>
              <p className="mt-3 text-white/60 max-w-md">
                {lang === "ar"
                  ? `شكرًا ${form.name?.split(" ")[0] || t.contact.thereFallback} — ${t.contact.successBody}`
                  : `Thanks ${form.name?.split(" ")[0] || t.contact.thereFallback} — ${t.contact.successBody}`}
              </p>
              <a
                href={contact.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold uppercase text-xs tracking-[0.2em] px-6 py-3"
                data-testid="contact-success-whatsapp-btn"
              >
                <MessageCircle size={14} /> {t.contact.chatWhatsapp}
              </a>
            </div>
          ) : (
            <>
              <div className="grid gap-5 md:grid-cols-2">
                <Field label={t.contact.fieldName}>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder={t.contact.placeholderName}
                    className="contact-input"
                    data-testid="contact-input-name"
                  />
                  {touched && !form.name && (
                    <p className="text-xs text-red-400 mt-1">{t.contact.errorName}</p>
                  )}
                </Field>
                <Field label={t.contact.fieldEmail}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder={t.contact.placeholderEmail}
                    className="contact-input"
                    dir="ltr"
                    data-testid="contact-input-email"
                  />
                  {touched && !form.email && (
                    <p className="text-xs text-red-400 mt-1">{t.contact.errorEmail}</p>
                  )}
                </Field>
              </div>
              <div className="grid gap-5 md:grid-cols-2 mt-5">
                <Field label={t.contact.fieldBusiness}>
                  <select
                    value={form.businessIndex}
                    onChange={updateIndex("businessIndex")}
                    className="contact-input"
                    data-testid="contact-select-business"
                  >
                    {businessTypes.map((b, idx) => (
                      <option key={b} value={idx} className="bg-agency-gray text-white">
                        {b}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t.contact.fieldService}>
                  <select
                    value={form.serviceIndex}
                    onChange={updateIndex("serviceIndex")}
                    className="contact-input"
                    data-testid="contact-select-service"
                  >
                    {serviceOptions.map((s, idx) => (
                      <option key={s} value={idx} className="bg-agency-gray text-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="mt-5">
                <Field label={t.contact.fieldMessage}>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder={t.contact.placeholderMessage}
                    className="contact-input resize-none"
                    data-testid="contact-input-message"
                  />
                  {touched && !form.message && (
                    <p className="text-xs text-red-400 mt-1">{t.contact.errorMessage}</p>
                  )}
                </Field>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-agency-gold text-black font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {loading ? t.contact.sending : t.contact.send} <Send size={14} />
                </button>
                <a
                  href={contact.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-emerald-500/40 text-emerald-300 font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-emerald-500/10 transition-colors"
                  data-testid="contact-whatsapp-button"
                >
                  <MessageCircle size={14} /> {t.contact.chatWhatsapp}
                </a>
              </div>
              {error && (
                <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-3">
                  {error}
                </p>
              )}
              <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/30">
                {t.contact.privacyNote}{" "}
                <Link
                  to="/privacy-policy"
                  className="underline underline-offset-2 hover:text-agency-gold transition-colors"
                  data-testid="contact-privacy-link"
                >
                  {t.contact.privacyLink}
                </Link>
              </p>
            </>
          )}
        </motion.form>
      </div>
      <style>{`
        .contact-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          color: #fafafa;
          padding: 14px 16px;
          font-size: 14px;
          border-radius: 8px;
          outline: none;
          transition: border-color 200ms ease, background 200ms ease;
        }
        .contact-input:focus {
          border-color: rgba(212, 175, 55, 0.6);
          background: rgba(255,255,255,0.05);
        }
        .contact-input::placeholder { color: rgba(255,255,255,0.3); }
        select.contact-input { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23D4AF37' d='M6 8L0 0h12z'/></svg>"); background-repeat: no-repeat; background-position: right 16px center; padding-right: 38px; }
        [dir="rtl"] select.contact-input { background-position: left 16px center; padding-right: 16px; padding-left: 38px; }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
